import { TEAM_STORE_METAOBJECT_QUERY } from '@theme/api-queries';
import { createStorefrontClient } from '@theme/storefront-api';

/**
 * @typedef {object} TeamProductData
 * @property {string} teamParentSku
 * @property {string} opportunityNumber
 * @property {Record<string, any>} [metaobject]
 * @property {Record<string, any>} [teamStoreCollection]
 */

/**
 * @typedef {object} MetaobjectField
 * @property {string} key
 * @property {string} value
 * @property {string} type
 */

/**
 * @typedef {object} TeamStoreMetafield
 * @property {string} key
 * @property {string} value
 */

/**
 * @typedef {object} TeamStoreCollectionReference
 * @property {string} id
 * @property {string|null} onlineStoreUrl
 * @property {(TeamStoreMetafield|null)[]|null} metafields
 */

/**
 * @typedef {object} TeamStoreCollectionField
 * @property {{ reference: TeamStoreCollectionReference|null }|null} team_store_collection
 */

/**
 * @typedef {object} TeamPdpMetaobjectNode
 * @property {MetaobjectField[]|null} fields
 * @property {{ reference: TeamStoreCollectionReference|null }|null} team_store_collection
 */

/**
 * @typedef {object} TeamPdpMetaobjectQueryData
 * @property {TeamPdpMetaobjectNode|null} metaobject
 */

/**
 * TeamPdpMetaobjectService
 * ------------------------
 * Encapsulates fetching and normalizing the Team PDP metaobject
 * its Team Store collection metafields, and wiring it into
 * teamProductData + DOM events.
 */
export class TeamPdpMetaobjectService {
  /**
   * @param {object} [opts]
   * @param {Window} [opts.win=window] - Window-like object (for testing).
   */
  constructor({ win = window } = {}) {
    this.client = createStorefrontClient({
      token: window.shopifyVariables.STOREFRONT_ACCESS_TOKEN,
    });
    this.win = win;
  }

  /**
   * @param {string} teamParentSku
   * Fetch Team PDP metaobject for given teamProductData and update it in-place.
   * @returns {Promise<{ metaobject: Record<string, any>|null, teamStoreCollection: Record<string, any>|null }>}
   */
  async load(teamParentSku) {
    const metaobjectHandle = this._buildHandle(teamParentSku);
    const query = TEAM_STORE_METAOBJECT_QUERY;
    const variables = { handle: metaobjectHandle };

    try {
      const result = await this.client.fetch(
        /** @type {string} */ (query),
        variables
      );

      this._markMediaLoaded();

      if (!result.ok || !result.data) {
        console.error(
          '[TeamPdpMetaobjectService] GraphQL failed:',
          result.error
        );
        return { metaobject: null, teamStoreCollection: null };
      }

      /** @type {TeamPdpMetaobjectQueryData} */
      const data = result.data;
      const node = data.metaobject;

      if (!node) {
        console.warn(
          '[TeamPdpMetaobjectService] No metaobject found for handle',
          metaobjectHandle
        );
        return { metaobject: null, teamStoreCollection: null };
      }

      const metaobject = this._normalizeMetaobjectFields(node.fields || []);
      const teamStoreCollection = this._normalizeTeamStoreCollection(
        node.team_store_collection
      );

      metaobject.handle = metaobjectHandle;
      metaobject.isTeamStoreClosed =
        this._isTeamStoreClosed(teamStoreCollection);

      if (metaobject) {
        this._dispatchMetaobjectEvent(metaobject, teamStoreCollection);
      }

      return { metaobject, teamStoreCollection };
    } catch (error) {
      this._markMediaLoaded();
      console.error('[TeamPdpMetaobjectService] Unexpected error:', error);
      return { metaobject: null, teamStoreCollection: null };
    }
  }

  /**
   * Build metaobject
   * @private
   * @param {string} teamParentSku

   * @returns {string}
   */
  _buildHandle(teamParentSku) {
    return `${teamParentSku}_${window.teamProductData.opportunityNumber}`;
  }

  /**
   * Convert metaobject.fields array into a simple key/value map.
   *
   * @private
   * @param {MetaobjectField[]} fields
   * @returns {Record<string, any>}
   */
  _normalizeMetaobjectFields(fields) {
    return fields.reduce((acc, field) => {
      if (!field || !field.key) return acc;
      acc[field.key] = field.value;
      return acc;
    }, /** @type {Record<string, any>} */ ({}));
  }

  /**
   * Normalize team_store_collection metafields into a plain object.
   *
   * @private
   * @param {{ reference: TeamStoreCollectionReference|null }|null} teamStoreCollectionField
   * @returns {Record<string, any>|null}
   */
  _normalizeTeamStoreCollection(teamStoreCollectionField) {
    const reference = teamStoreCollectionField?.reference;
    if (!reference) return null;

    const metafieldsArray = reference.metafields || [];

    /** @type {Record<string, any>} */
    const normalized = metafieldsArray.reduce((acc, field) => {
      if (!field || !field.key || field.value == null) return acc;
      acc[field.key] = field.value;
      return acc;
    }, /** @type {Record<string, any>} */ ({}));

    normalized.id = reference.id;
    normalized.url = reference.onlineStoreUrl;

    return normalized;
  }

  /**
   * Determine if team store is closed based on start/end dates.
   *
   * @private
   * @param {Record<string, any>|null} teamStoreCollection
   * @returns {boolean}
   */
  _isTeamStoreClosed(teamStoreCollection) {
    const now = Date.now();

    const start = teamStoreCollection?.start_date
      ? new Date(teamStoreCollection.start_date).getTime()
      : null;
    const end = teamStoreCollection?.end_date
      ? new Date(teamStoreCollection.end_date).getTime()
      : null;

    if (!start || !end) return true;
    if (end && now > end) return true;
    if (start && now < start) return true;

    return false;
  }

  /**
   * Mark product media lists as loaded (remove "loading", add "loaded").
   *
   * @private
   */
  _markMediaLoaded() {
    const doc = this.win.document;
    const elements = doc.querySelectorAll('.product__media-list');
    elements.forEach((element) => {
      element.classList.remove('loading');
      element.classList.add('loaded');
    });
  }

  /**
   * Dispatch a global event once metaobject & collection are ready.
   *
   * @private
   * @param {Record<string, any>} metaobject
   * @param {Record<string, any>|null} teamStoreCollection
   */
  _dispatchMetaobjectEvent(metaobject, teamStoreCollection) {
    window.TeamStoreMetaobjects ??= {};

    if (metaobject.handle) {
      window.TeamStoreMetaobjects[metaobject.handle] = {
        metaobject,
        teamStoreCollection,
      };
    }

    const event = new CustomEvent('metaobjectDataRetrieved', {
      detail: { metaobject, teamStoreCollection },
    });
    this.win.dispatchEvent(event);
  }
}


/**
 * @typedef {object} MetaobjectEventDetail
 * @property {string} handle
 * @property {Record<string, any>} metaobject
 * @property {Record<string, any>|null} [teamStoreCollection]
 */

/**
 * Subscribe to metaobjectDataRetrieved events for a specific handle.
 *
 * Usage example
 * --------------------------------------------
 * const handle = `${bundle_product.metafields.custom.parent_sku}_${window.teamProductData.opportunityNumber}`;
 *
 * const unsubscribe = window.TeamStoreEvents.subscribeToMetaobject(
 *   handle,
 *   (metaobject, teamStoreCollection) => {
 *     console.log('Metaobject event received:', { metaobject, teamStoreCollection });
 *   }
 * );
 *
 * // Later if needed:
 * // unsubscribe();
 *
 * @param {string} handle
 * @param {(metaobject: Record<string, any>, teamStoreCollection: Record<string, any>|null) => void} callback
 * @returns {() => void} unsubscribe function
 */
function subscribeToMetaobject(handle, callback) {
  /** @param {CustomEvent<MetaobjectEventDetail>} event */
  const listener = (event) => {
    const detail = event.detail;

    if (!detail || detail.metaobject.handle !== handle) return;
    callback(detail.metaobject, detail.teamStoreCollection || null);
  };

  window.addEventListener('metaobjectDataRetrieved', listener);

  return () => {
    window.removeEventListener('metaobjectDataRetrieved', listener);
  };
}

window.TeamStoreEvents ??= {};
window.TeamStoreServices ??= {};

window.TeamStoreEvents.subscribeToMetaobject = subscribeToMetaobject;
window.TeamStoreServices.TeamPdpMetaobjectService = TeamPdpMetaobjectService;